import React, { useState } from 'react';
import { Box, Button, Typography, Popper, ClickAwayListener, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, StaticDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { toast } from 'react-toastify';
import 'dayjs/locale/en';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const CalendarPage = ({ onDateChange }) => {
    const now = dayjs.tz(new Date(), 'Asia/Kolkata');
    const currentYear = now.year();
    const [anchorEl, setAnchorEl] = useState(null);
    const [startDate, setStartDate] = useState(dayjs(`${currentYear}-04-01`));
    const [endDate, setEndDate] = useState(dayjs(`${currentYear + 1}-04-01`).subtract(1, 'day'));
    const [calendarDate, setCalendarDate] = useState(dayjs(`${currentYear}-04-01`));
    const [selectedPreset, setSelectedPreset] = useState('This fiscal year');
    const [isSelectingStart, setIsSelectingStart] = useState(true);

    const presetOptions = [
        'Today', 'Yesterday', 'This week (Mon - Today)', 'Last 7 days',
        'Last week (Mon - Sun)', 'Last 14 days', 'This month', 'Last 30 days',
        'Last month', 'All time', '30 days up to today', '30 days up to yesterday',
        'This fiscal year'
    ];

    const getPresetRange = (preset) => {
        switch (preset) {
            case 'Today':
                return { start: now, end: now };
            case 'Yesterday':
                return { start: now.subtract(1, 'day'), end: now.subtract(1, 'day') };
            case 'This week (Mon - Today)':
                return { start: now.startOf('week'), end: now };
            case 'Last 7 days':
                return { start: now.subtract(6, 'day'), end: now };
            case 'Last week (Mon - Sun)':
                return { start: now.subtract(1, 'week').startOf('week'), end: now.subtract(1, 'week').endOf('week') };
            case 'Last 14 days':
                return { start: now.subtract(13, 'day'), end: now };
            case 'This month':
                return { start: now.startOf('month'), end: now };
            case 'Last 30 days':
                return { start: now.subtract(29, 'day'), end: now };
            case 'Last month':
                return { start: now.subtract(1, 'month').startOf('month'), end: now.subtract(1, 'month').endOf('month') };
            case 'All time':
                return { start: dayjs.tz('2010-01-01 00:00', 'Asia/Kolkata'), end: now };
            case '30 days up to today':
                return { start: now.subtract(29, 'day'), end: now };
            case '30 days up to yesterday':
                return { start: now.subtract(30, 'day'), end: now.subtract(1, 'day') };
            case 'This fiscal year':
                return { start: dayjs(`${currentYear}-04-01`), end: dayjs(`${currentYear + 1}-04-01`).subtract(1, 'day') };
            default:
                return { start: startDate, end: endDate };
        }
    };

    const handlePresetClick = (preset) => {
        const { start, end } = getPresetRange(preset);
        setStartDate(start);
        setEndDate(end);
        setSelectedPreset(preset);
        setAnchorEl(null);
        onDateChange(start, end);
    };

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePrevMonth = () => {
        setCalendarDate(calendarDate.subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setCalendarDate(calendarDate.add(1, 'month'));
    };

    const handleStartDateChange = (newValue) => {
        const newStartDate = newValue || startDate;
        if (newStartDate.isAfter(endDate)) {
            toast.error('Start date cannot be after end date');
            return;
        }
        setStartDate(newStartDate);
        setSelectedPreset('Custom');
        onDateChange(newStartDate, endDate);
    };

    const handleEndDateChange = (newValue) => {
        const newEndDate = newValue || endDate;
        if (newEndDate.isBefore(startDate)) {
            toast.error('End date cannot be before start date');
            return;
        }
        setEndDate(newEndDate);
        setSelectedPreset('Custom');
        onDateChange(startDate, newEndDate);
    };

    const handleCalendarDateChange = (newValue) => {
        const selectedDate = newValue || calendarDate;
        if (isSelectingStart) {
            setStartDate(selectedDate);
            if (selectedDate.isAfter(endDate)) {
                setEndDate(selectedDate);
            }
            setIsSelectingStart(false);
            onDateChange(selectedDate, endDate);
        } else {
            if (selectedDate.isBefore(startDate)) {
                toast.error('End date cannot be before start date');
                return;
            }
            setEndDate(selectedDate);
            setIsSelectingStart(true);
            onDateChange(startDate, selectedDate);
        }
        setCalendarDate(selectedDate);
        setSelectedPreset('Custom');
    };

    const open = Boolean(anchorEl);
    const id = open ? 'calendar-popper' : undefined;

    const rangeText = startDate.isSame(endDate)
        ? startDate.format('DD MMMM YYYY')
        : `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD MMMM YYYY')}`;

    return (
        <Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                sx={{ textTransform: 'none', minWidth: '200px', borderRadius: 1 }}
            >
                {rangeText}
            </Button>
            <Popper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box sx={{ display: 'flex', p: 1, bgcolor: 'white', borderRadius: 2, boxShadow: 3, maxWidth: 600 }}>
                        <Box sx={{ width: 200, p: 1, borderRight: '1px solid #e0e0e0' }}>
                            {presetOptions.map((option) => (
                                <Button
                                    key={option}
                                    variant={option === selectedPreset ? 'contained' : 'text'}
                                    onClick={() => handlePresetClick(option)}
                                    sx={{
                                        display: 'block',
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        mb: 1,
                                        width: '100%',
                                        backgroundColor: option === selectedPreset ? '#0288d1' : 'transparent',
                                        color: option === selectedPreset ? '#fff' : 'inherit',
                                        '&:hover': {
                                            backgroundColor: option === selectedPreset ? '#0277bd' : 'rgba(0,0,0,0.04)',
                                        },
                                    }}
                                >
                                    {option}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ p: 1, flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mr: 2 }}>Custom</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start date*"
                                        value={startDate}
                                        format="DD/MM/YYYY"
                                        onChange={handleStartDateChange}
                                        renderInput={(params) => <TextField {...params} size="small" sx={{ mr: 1, width: 130 }} />}
                                    />
                                    <Typography sx={{ mx: 1 }}>-</Typography>
                                    <DatePicker
                                        label="End date*"
                                        value={endDate}
                                        format="DD/MM/YYYY"
                                        onChange={handleEndDateChange}
                                        renderInput={(params) => <TextField {...params} size="small" sx={{ width: 130 }} />}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <IconButton onClick={handlePrevMonth} size="small" disabled={calendarDate.isBefore(now.subtract(4, 'month'), 'month')}>
                                    <ArrowBackIosIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="subtitle1" sx={{ mx: 1 }}>
                                    {calendarDate.format('MMMM YYYY')}
                                </Typography>
                                <IconButton onClick={handleNextMonth} size="small" disabled={calendarDate.isAfter(now, 'month')}>
                                    <ArrowForwardIosIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <StaticDatePicker
                                    displayStaticWrapperAs="desktop"
                                    value={calendarDate}
                                    onChange={handleCalendarDateChange}
                                    renderInput={(params) => <TextField {...params} />}
                                    sx={{ '& .MuiPickersCalendarHeader-root': { display: 'none' } }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default CalendarPage;